import { createSlice } from '@reduxjs/toolkit'
import { buildWikiExtraReducers } from './wiki/wikiReducers'

// Re-export thunks from modular files
export {
  fetchSpaces, createSpace, updateSpace, deleteSpace,
  updateSpaceCover, uploadSpaceCoverImage, updateSpaceOrder,
  archiveSpace, restoreSpace, fetchArchivedSpaces
} from './wiki/wikiSpacesThunks'

export {
  fetchPages, fetchPageContent, createPage, updatePage,
  deletePage, movePage, togglePageFavorite, updatePageOrder,
  archivePage, restorePage, fetchArchivedPages
} from './wiki/wikiPagesThunks'

export { searchPages } from './wiki/wikiSearchThunks'
export { recordPageView, saveScrollPosition } from './wiki/wikiReadingThunks'

// Re-export selectors from modular file
export {
  selectSpacesRaw, selectPages, selectPageContents,
  selectSearchQuery, selectSearchResults,
  selectCurrentSpaceId, selectCurrentPageId,
  selectWikiLoading, selectWikiError, selectWikiInitialized,
  selectPagesMap, selectSpacesMap, selectPagesBySpaceMap,
  selectSpaces, selectCurrentSpace, selectCurrentPage,
  selectPageContent, selectPagesBySpace, selectPageTree,
  selectFavoritePages, selectRecentlyUpdatedPages,
  selectBreadcrumbs, selectSpacesByContext,
  selectSpacesByProjectId, selectSpacesByAnnualGoalId,
  selectPagesByProjectId, selectPagesByAnnualGoalId, selectPagesByTaskId,
  selectSpaceBySlug, selectPageBySlugInSpace,
  selectContinueReadingPage, selectRecentlyReadPages,
  selectArchivedSpaces, selectArchivedPages, selectArchivedRootPages,
  selectArchivedItemCount
} from './wiki/wikiSelectors'

// === INITIAL STATE ===
const initialState = {
  spaces: [],
  pages: [],                // Metadata only (no content)
  pageContents: {},         // { [pageId]: { content, loadedAt } }
  archivedSpaces: [],       // Archived spaces (loaded on-demand for Archived view)
  archivedPages: [],        // Archived pages metadata (loaded on-demand)
  searchQuery: '',
  searchResults: [],

  loading: {
    spaces: false,
    pages: false,
    pageContent: false,
    search: false,
    createSpace: false,
    createPage: false,
    updatePage: false,
    updateSpace: false,
    deleteSpace: false,
    deletePage: false,
    movePage: false,
    toggleFavorite: false,
    archive: false,
    archived: false
  },
  error: {
    spaces: null,
    pages: null,
    pageContent: null,
    search: null,
    archive: null,
    archived: null
  },
  initialized: {
    spaces: false,
    pages: false,
    archived: false
  },

  // UI state
  currentSpaceId: null,
  currentPageId: null
}

// === SLICE ===
const wikiSlice = createSlice({
  name: 'wiki',
  initialState,
  reducers: {
    // Bootstrap data setters
    setSpaces: (state, action) => {
      state.spaces = action.payload
      state.initialized.spaces = true
    },
    setPages: (state, action) => {
      state.pages = action.payload
      state.initialized.pages = true
    },

    // Navigation
    setCurrentSpace: (state, action) => {
      state.currentSpaceId = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPageId = action.payload
    },

    // Clear state (logout)
    clearWiki: (state) => {
      state.spaces = []
      state.pages = []
      state.pageContents = {}
      state.archivedSpaces = []
      state.archivedPages = []
      state.searchQuery = ''
      state.searchResults = []
      state.initialized.spaces = false
      state.initialized.pages = false
      state.initialized.archived = false
      state.currentSpaceId = null
      state.currentPageId = null
    },

    // Clear errors
    clearError: (state, action) => {
      const { dataType } = action.payload
      if (state.error[dataType]) {
        state.error[dataType] = null
      }
    },

    // Set search query
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
    },

    // Clear search results
    clearSearchResults: (state) => {
      state.searchQuery = ''
      state.searchResults = []
    },

    // Optimistic updates
    updatePageOptimistic: (state, action) => {
      const { pageId, updates } = action.payload
      const pageIndex = state.pages.findIndex(p => p.id === pageId)
      if (pageIndex !== -1) {
        state.pages[pageIndex] = { ...state.pages[pageIndex], ...updates }
      }
      // Also update content cache if content was updated
      if (updates.content !== undefined && state.pageContents[pageId]) {
        state.pageContents[pageId] = {
          ...state.pageContents[pageId],
          content: updates.content,
          loadedAt: Date.now()
        }
      }
    },

    // Add page to local state (used for optimistic updates or realtime sync)
    addPage: (state, action) => {
      state.pages.push(action.payload)
    },

    // Add space to local state (used for optimistic updates or realtime sync)
    addSpace: (state, action) => {
      state.spaces.push(action.payload)
    },

    // Update space in local state
    updateSpaceLocal: (state, action) => {
      const updatedSpace = action.payload
      const index = state.spaces.findIndex(s => s.id === updatedSpace.id)
      if (index !== -1) {
        state.spaces[index] = updatedSpace
      }
    },

    // Optimistic space reorder
    reorderSpacesOptimistic: (state, action) => {
      const { spaces } = action.payload
      // Replace spaces with new order, preserving display_order values
      state.spaces = spaces.map((space, index) => ({
        ...space,
        display_order: index
      }))
    },

    // Optimistic page reorder within same parent
    reorderPagesOptimistic: (state, action) => {
      const { pages } = action.payload
      // Update display_order for pages that were reordered
      const reorderedPageIds = new Set(pages.map(p => p.id))
      state.pages = state.pages.map(page => {
        if (reorderedPageIds.has(page.id)) {
          const reorderedPage = pages.find(p => p.id === page.id)
          const newIndex = pages.indexOf(reorderedPage)
          return { ...page, display_order: newIndex }
        }
        return page
      })
    },

    // Optimistic page parent change
    movePageOptimistic: (state, action) => {
      const { pageId, newParentId, newDisplayOrder } = action.payload
      const pageIndex = state.pages.findIndex(p => p.id === pageId)
      if (pageIndex !== -1) {
        state.pages[pageIndex] = {
          ...state.pages[pageIndex],
          parent_page_id: newParentId,
          display_order: newDisplayOrder
        }
      }
    }
  },
  extraReducers: buildWikiExtraReducers
})

// === ACTIONS ===
export const {
  setSpaces,
  setPages,
  setCurrentSpace,
  setCurrentPage,
  clearWiki,
  clearError,
  setSearchQuery,
  clearSearchResults,
  updatePageOptimistic,
  addPage,
  addSpace,
  updateSpaceLocal,
  reorderSpacesOptimistic,
  reorderPagesOptimistic,
  movePageOptimistic
} = wikiSlice.actions

export default wikiSlice.reducer
